import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Shield, MessageCircle, Heart, MapPin, Sparkles, User, Mail, Send } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { WHATSAPP_PHONE_NUMBER, TELEGRAM_BOT_USERNAME } from '../config';

interface RegistrationPromptProps {
  isOpen: boolean;
  onClose: () => void;
  variant: 'google_auth' | 'whatsapp_verify' | 'wait_only';
  trigger?: 'save' | 'chat_limit' | 'fare_limit' | 'trip_optimize' | 'profile' | 'trip_create' | 'trip_summary_limit' | 'quick_tips_limit';
  waitMinutes?: number;
}

export default function RegistrationPrompt({
  isOpen,
  onClose,
  variant,
  trigger = 'save',
  waitMinutes,
}: RegistrationPromptProps) {
  const { signInWithGoogle, authState, startVerificationListener } = useAuth();
  const navigate = useNavigate();
  const [googleError, setGoogleError] = useState('');
  const [dragStartY, setDragStartY] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, variant, trigger]);

  if (!isOpen) return null;

  const handleGoogleSuccess = async (credentialResponse: any) => {
    if (!credentialResponse || !credentialResponse.credential) {
      setGoogleError('Sign-in failed. No credential received.');
      return;
    }
    
    try {
      setGoogleError('');
      const result = await signInWithGoogle(credentialResponse.credential);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign-in failed. Please try again.';
      setGoogleError(errorMessage);
    }
  };

  const handleGoogleError = (error?: any) => {
    setGoogleError('Google sign-in failed. Please try again.');
  };

  const handleWhatsAppVerify = () => {
    // Start on-demand listener when user initiates verification
    startVerificationListener('whatsapp');
    
    const userEmail = authState.user?.profile?.email || '';
    const whatsappNumber = WHATSAPP_PHONE_NUMBER;
    const message = encodeURIComponent(userEmail);
    window.open(`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
    onClose();
  };

  const handleTelegramVerify = () => {
    // Start on-demand listener when user initiates verification
    startVerificationListener('telegram');
    
    const userEmail = authState.user?.profile?.email || '';
    const botUsername = TELEGRAM_BOT_USERNAME;
    window.open(`https://t.me/${botUsername}?start=${userEmail}`, '_blank');
    onClose();
  };

  const handleEmailAuth = () => {
    onClose();
    navigate('/email-auth');
  };

  // Handle drag to dismiss on mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setDragStartY(e.touches[0].clientY);
    setDragOffset(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartY === null) return;
    
    const currentY = e.touches[0].clientY;
    const offset = currentY - dragStartY;
    
    // Only allow downward drag
    if (offset > 0) {
      setDragOffset(offset);
    }
  };

  const handleTouchEnd = () => {
    if (dragOffset > 100) {
      // Dragged down more than 100px, close the modal
      onClose();
    }
    // Reset drag state
    setDragStartY(null);
    setDragOffset(0);
  };

  // Handle "Maybe later" button tap
  const handleMaybeLater = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const getTriggerContent = () => {
    if (variant === 'google_auth') {
      switch (trigger) {
        case 'trip_create':
          return {
            icon: <MapPin className="w-12 h-12 text-purple-500 mx-auto mb-4" />,
            title: 'Plan Your Dream Journey',
            description: 'Sign in with Google to create travel plans, organize your journey, and make the most of your Sri Lankan adventure!',
          };
        case 'profile':
          return {
            icon: <User className="w-12 h-12 text-blue-500 mx-auto mb-4" />,
            title: 'Access Your Profile',
            description: 'Sign in with Google to manage your profile, saved places, and travel preferences.',
          };
        case 'save':
          return {
            icon: <Heart className="w-12 h-12 text-red-500 mx-auto mb-4" />,
            title: 'Save Your Favorites',
            description: 'Sign in to save places, create travel plans, and get personalized recommendations.',
          };
        case 'chat_limit':
          return {
            icon: <MessageCircle className="w-12 h-12 text-blue-500 mx-auto mb-4" />,
            title: 'Chat Limit Reached',
            description: 'You\'ve reached your 5 free conversations. Sign in with Google to continue exploring Sri Lanka!',
          };
        case 'fare_limit':
          return {
            icon: <MapPin className="w-12 h-12 text-green-500 mx-auto mb-4" />,
            title: 'Fare Check Limit Reached',
            description: 'You\'ve used your 2 free fare checks. Sign in with Google to calculate more routes!',
          };
        case 'quick_tips_limit':
          return {
            icon: <MessageCircle className="w-12 h-12 text-blue-500 mx-auto mb-4" />,
            title: 'Quick Tips Limit Reached',
            description: 'You\'ve used your 10 free Quick Tips per hour. Sign in with Google to unlock 20 per hour!',
          };
        default:
          return {
            icon: <Shield className="w-12 h-12 text-green-500 mx-auto mb-4" />,
            title: 'Sign In to Continue',
            description: 'Create your free account to unlock all features.',
          };
      }
    } else if (variant === 'wait_only') {
      // For verified users who hit rate limits - no action needed, just wait
      switch (trigger) {
        case 'chat_limit':
          return {
            icon: <MessageCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />,
            title: 'Hourly Limit Reached',
            description: waitMinutes 
              ? `You've reached your hourly chat limit. Please wait ${waitMinutes} minute${waitMinutes > 1 ? 's' : ''} to continue chatting.`
              : 'You\'ve reached your hourly chat limit. Please try again later.',
          };
        case 'fare_limit':
          return {
            icon: <MapPin className="w-12 h-12 text-amber-500 mx-auto mb-4" />,
            title: 'Hourly Limit Reached',
            description: waitMinutes
              ? `You've reached your hourly fare check limit. Please wait ${waitMinutes} minute${waitMinutes > 1 ? 's' : ''} to check more fares.`
              : 'You\'ve reached your hourly fare check limit. Please try again later.',
          };
        case 'trip_summary_limit':
          return {
            icon: <Sparkles className="w-12 h-12 text-amber-500 mx-auto mb-4" />,
            title: 'Hourly Limit Reached',
            description: waitMinutes
              ? `You've reached your hourly trip summary limit. Please wait ${waitMinutes} minute${waitMinutes > 1 ? 's' : ''} to generate more summaries.`
              : 'You\'ve reached your hourly trip summary limit. Please try again later.',
          };
        case 'quick_tips_limit':
          return {
            icon: <MessageCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />,
            title: 'Hourly Limit Reached',
            description: waitMinutes
              ? `You've reached your hourly Quick Tips limit. Please wait ${waitMinutes} minute${waitMinutes > 1 ? 's' : ''} to continue.`
              : 'You\'ve reached your hourly Quick Tips limit. Please try again later.',
          };
        default:
          return {
            icon: <Shield className="w-12 h-12 text-amber-500 mx-auto mb-4" />,
            title: 'Hourly Limit Reached',
            description: waitMinutes
              ? `You've reached your hourly limit. Please wait ${waitMinutes} minute${waitMinutes > 1 ? 's' : ''} to continue.`
              : 'You\'ve reached your hourly limit. Please try again later.',
          };
      }
    } else {
      // whatsapp_verify
      switch (trigger) {
        case 'chat_limit':
          return {
            icon: <MessageCircle className="w-12 h-12 text-blue-500 mx-auto mb-4" />,
            title: 'Chat Limit Reached',
            description: 'You\'ve reached your hourly chat limit. Verify via WhatsApp or Telegram for more conversations!',
          };
        case 'fare_limit':
          return {
            icon: <MapPin className="w-12 h-12 text-green-500 mx-auto mb-4" />,
            title: 'Fare Check Limit Reached',
            description: 'You\'ve reached your hourly fare check limit. Verify via WhatsApp or Telegram for more access!',
          };
        case 'quick_tips_limit':
          return {
            icon: <MessageCircle className="w-12 h-12 text-blue-500 mx-auto mb-4" />,
            title: 'Quick Tips Limit Reached',
            description: 'You\'ve reached your hourly Quick Tips limit. Verify via WhatsApp or Telegram to unlock 50 per hour!',
          };
        case 'trip_optimize':
          return {
            icon: <Sparkles className="w-12 h-12 text-purple-500 mx-auto mb-4" />,
            title: 'Unlock Advanced Planning',
            description: 'Verify via WhatsApp or Telegram to access our AI-powered travel planning features!',
          };
        default:
          return {
            icon: <Shield className="w-12 h-12 text-green-500 mx-auto mb-4" />,
            title: 'Get Full Access',
            description: 'Verify via WhatsApp or Telegram for more access to all features.',
          };
      }
    }
  };

  const content = getTriggerContent();

  // Render modal at root level using Portal to avoid parent CSS constraints
  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[200] transition-opacity"
        onClick={onClose}
      />

      {/* Desktop Modal */}
      <div className="hidden md:flex fixed inset-0 z-[210] items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-fade-in pointer-events-auto">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>

          {/* Content */}
          <div className="text-center">
            {content.icon}
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {content.title}
            </h2>
            <p className="text-gray-600 mb-6">
              {content.description}
            </p>

            {variant === 'google_auth' ? (
              <>
                <div 
                  className="flex justify-center"
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="continue_with"
                    size="large"
                    width="384"
                    logo_alignment="left"
                  />
                </div>
                {googleError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{googleError}</p>
                  </div>
                )}
                
                {/* OR Divider */}
                <div className="flex items-center my-4">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="px-3 text-sm text-gray-500">OR</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Email Auth Button */}
                <button
                  onClick={handleEmailAuth}
                  className="w-full py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center gap-2"
                >
                  <Mail size={20} />
                  Continue with Email
                </button>

                <div className="text-xs text-gray-500 mt-4 space-y-2">
                  <p>
                    By continuing, you agree to Hey Sri Lanka's{' '}
                    <a href="/terms" className="text-gray-600 hover:text-gray-900 underline transition-colors">
                      Terms of Service
                    </a>
                  </p>
                  <div className="flex items-center justify-center gap-3 text-[11px]">
                    <a
                      href="/privacy"
                      className="text-gray-600 hover:text-gray-900 underline transition-colors"
                    >
                      Privacy Policy
                    </a>
                    <span className="text-gray-300">•</span>
                    <a
                      href="/terms"
                      className="text-gray-600 hover:text-gray-900 underline transition-colors"
                    >
                      Terms of Service
                    </a>
                  </div>
                </div>
              </>
            ) : variant === 'wait_only' ? (
              <>
                {/* Simple wait message for verified users - no action buttons */}
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800">
                    ⏱️ Your limit will reset in about an hour. Thank you for your patience!
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Got it
                </button>
              </>
            ) : (
              <>
                {waitMinutes && waitMinutes > 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      Or wait <span className="font-semibold">{waitMinutes} minute{waitMinutes > 1 ? 's' : ''}</span> to continue
                    </p>
                  </div>
                )}
                <div className="flex gap-3 mb-4">
                  <button
                    onClick={handleWhatsAppVerify}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                  >
                    <MessageCircle size={20} />
                    WhatsApp
                  </button>
                  <button
                    onClick={handleTelegramVerify}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                  >
                    <Send size={20} />
                    Telegram
                  </button>
                </div>
                <div className="text-xs text-gray-500 space-y-2">
                  <p>
                    By continuing, you agree to Hey Sri Lanka's{' '}
                    <a href="/terms" className="text-gray-600 hover:text-gray-900 underline transition-colors">
                      Terms of Service
                    </a>
                  </p>
                  <div className="flex items-center justify-center gap-3 text-[11px]">
                    <a
                      href="/privacy"
                      className="text-gray-600 hover:text-gray-900 underline transition-colors"
                    >
                      Privacy Policy
                    </a>
                    <span className="text-gray-300">•</span>
                    <a
                      href="/terms"
                      className="text-gray-600 hover:text-gray-900 underline transition-colors"
                    >
                      Terms of Service
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Bottom Sheet */}
      <div 
        className="md:hidden fixed inset-x-0 bottom-0 z-[210] animate-slide-up transition-transform duration-200"
        style={{ 
          transform: `translateY(${dragOffset}px)`,
          transition: dragStartY !== null ? 'none' : 'transform 0.2s ease-out'
        }}
      >
        <div className="bg-white rounded-t-3xl shadow-2xl pb-8">
          {/* Draggable Header Area - includes handle, icon, and title */}
          <div 
            className="cursor-grab active:cursor-grabbing touch-none pt-3 pb-4 px-6"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Handle bar */}
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-6" />

            {/* Draggable header content */}
            <div className="text-center">
              {content.icon}
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {content.title}
              </h2>
            </div>
          </div>

          {/* Non-draggable Content */}
          <div className="text-center px-6">
            <p className="text-gray-600 mb-6 text-sm">
              {content.description}
            </p>

            {variant === 'google_auth' ? (
              <>
                <div 
                  className="flex justify-center mb-3"
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="continue_with"
                    size="large"
                    width="300"
                    logo_alignment="left"
                  />
                </div>
                {googleError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{googleError}</p>
                  </div>
                )}
                
                {/* OR Divider */}
                <div className="flex items-center my-4">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="px-3 text-sm text-gray-500">OR</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Email Auth Button */}
                <button
                  onClick={handleEmailAuth}
                  className="w-full py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg active:bg-gray-50 transition-all flex items-center justify-center gap-2 mb-3"
                >
                  <Mail size={20} />
                  Continue with Email
                </button>

                <div className="text-xs text-gray-500 mt-3 space-y-2 mb-3">
                  <p>
                    By continuing, you agree to Hey Sri Lanka's{' '}
                    <a href="/terms" className="text-gray-600 hover:text-gray-900 underline transition-colors">
                      Terms of Service
                    </a>
                  </p>
                  <div className="flex items-center justify-center gap-3 text-[11px]">
                    <a
                      href="/privacy"
                      className="text-gray-600 hover:text-gray-900 underline transition-colors"
                    >
                      Privacy Policy
                    </a>
                    <span className="text-gray-300">•</span>
                    <a
                      href="/terms"
                      className="text-gray-600 hover:text-gray-900 underline transition-colors"
                    >
                      Terms of Service
                    </a>
                  </div>
                </div>

                <button
                  onClick={handleMaybeLater}
                  onTouchEnd={handleMaybeLater}
                  className="w-full text-gray-600 font-medium py-3 active:text-gray-800 transition-colors touch-manipulation"
                >
                  Maybe later
                </button>
              </>
            ) : variant === 'wait_only' ? (
              <>
                {/* Simple wait message for verified users - no action buttons */}
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 text-sm">
                    ⏱️ Your limit will reset in about an hour. Thank you for your patience!
                  </p>
                </div>
                <button
                  onClick={onClose}
                  onTouchEnd={(e) => { e.preventDefault(); onClose(); }}
                  className="w-full bg-gray-900 active:bg-gray-800 text-white font-semibold py-3 px-4 rounded-lg transition-all shadow-md active:shadow-lg touch-manipulation"
                >
                  Got it
                </button>
              </>
            ) : (
              <>
                {waitMinutes && waitMinutes > 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      Or wait <span className="font-semibold">{waitMinutes} minute{waitMinutes > 1 ? 's' : ''}</span> to continue
                    </p>
                  </div>
                )}
                <div className="flex gap-3 mb-3">
                  <button
                    onClick={handleWhatsAppVerify}
                    className="flex-1 bg-green-500 active:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-md active:shadow-lg"
                  >
                    <MessageCircle size={20} />
                    WhatsApp
                  </button>
                  <button
                    onClick={handleTelegramVerify}
                    className="flex-1 bg-blue-500 active:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-md active:shadow-lg"
                  >
                    <Send size={20} />
                    Telegram
                  </button>
                </div>
                <div className="text-xs text-gray-500 space-y-2 mb-3">
                  <p>
                    By continuing, you agree to Hey Sri Lanka's{' '}
                    <a href="/terms" className="text-gray-600 hover:text-gray-900 underline transition-colors">
                      Terms of Service
                    </a>
                  </p>
                  <div className="flex items-center justify-center gap-3 text-[11px]">
                    <a
                      href="/privacy"
                      className="text-gray-600 hover:text-gray-900 underline transition-colors"
                    >
                      Privacy Policy
                    </a>
                    <span className="text-gray-300">•</span>
                    <a
                      href="/terms"
                      className="text-gray-600 hover:text-gray-900 underline transition-colors"
                    >
                      Terms of Service
                    </a>
                  </div>
                </div>

                <button
                  onClick={handleMaybeLater}
                  onTouchEnd={handleMaybeLater}
                  className="w-full text-gray-600 font-medium py-3 active:text-gray-800 transition-colors touch-manipulation"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

